import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * Scrolls to the top on every route change (or to the `#hash` target when
 * there is one). Browser back/forward keeps the native scroll position.
 * Query-only changes (e.g. `?car=` for the catalog modal) don't scroll.
 */
export function useScrollToTop() {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()
  // Read, not subscribed: a PUSH→REPLACE switch on the same path is not a route change.
  const navigationTypeRef = useRef(navigationType)
  navigationTypeRef.current = navigationType

  useEffect(() => {
    if (navigationTypeRef.current === 'POP') return

    if (hash) {
      // Lazy pages may not have rendered the target yet — retry for a few frames.
      let attempts = 0
      const tryScroll = () => {
        const target = document.getElementById(hash.slice(1))
        if (target) {
          target.scrollIntoView({ block: 'start' })
        } else if (attempts++ < 20) {
          requestAnimationFrame(tryScroll)
        }
      }
      tryScroll()
      return
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [pathname, hash])
}
